/*
 * Figma `118:337` — Audit Timeline Event, and the Audit Log's before/after diff (`125:507`).
 *
 * Every high-risk action in the v3 contract has to leave evidence someone can read later: who did
 * it, to what, when, and what the value was before. This is that evidence, rendered.
 *
 * An ordered list, newest first, because the order *is* the information — it is how you tell a retry
 * from a second attempt. Times are absolute as well as relative: "2 ชั่วโมงที่แล้ว" is friendly and
 * useless in an incident review, where the question is which of two events came first.
 *
 * `redacted` is not cosmetic. An audit entry for a credential change must prove the change happened
 * without reprinting the secret into a screen someone will screenshot, so a redacted field shows
 * that it changed and refuses to show to what.
 */
import type { ReactNode } from 'react';
import { StatusBadge } from './StatusBadge';
import type { BadgeTone } from './StatusBadge';

export interface AuditChange {
  field: string;
  before?: string;
  after?: string;
  /** Shows that the value changed without printing either side of it. */
  redacted?: boolean;
}

export interface AuditEvent {
  id: string;
  /** Who did it — a person, or the system where no person did. */
  actor: string;
  /** What they did, in the past tense: "เปลี่ยนบัญชีรับเงิน". */
  action: string;
  /** What it was done to: "PromptPay · 081-234-5678". */
  target?: string;
  /** Absolute, already formatted for display. */
  at: string;
  /** Optional, e.g. "2 ชั่วโมงที่แล้ว". */
  relative?: string;
  tone?: BadgeTone;
  /** A short status word — "สำเร็จ", "ถูกปฏิเสธ". */
  outcome?: string;
  changes?: readonly AuditChange[];
  note?: ReactNode;
}

export function AuditTimelineEvent({ event }: { event: AuditEvent }) {
  return (
    <li className="jt-audit-event">
      <span className="jt-audit-rail" aria-hidden="true" />
      <div className="jt-audit-body">
        <div className="jt-audit-head">
          <span className="jt-audit-action">{event.action}</span>
          {event.outcome && <StatusBadge tone={event.tone ?? 'neutral'}>{event.outcome}</StatusBadge>}
        </div>
        <p className="jt-audit-meta">
          {event.actor}
          {event.target && <> · <span className="jt-audit-target">{event.target}</span></>}
          {' · '}
          <time dateTime={event.at}>{event.at}</time>
          {event.relative && <> ({event.relative})</>}
        </p>
        {event.changes && event.changes.length > 0 && (
          <dl className="jt-audit-changes">
            {event.changes.map((change) => (
              <div key={change.field} className="jt-audit-change">
                <dt>{change.field}</dt>
                <dd>
                  {change.redacted ? (
                    <span className="jt-audit-redacted">เปลี่ยนแล้ว — ค่าถูกปิดไว้</span>
                  ) : (
                    <>
                      <span className="jt-audit-before">{change.before ?? '—'}</span>
                      <span className="jt-audit-arrow" aria-label="เปลี่ยนเป็น">→</span>
                      <span className="jt-audit-after">{change.after ?? '—'}</span>
                    </>
                  )}
                </dd>
              </div>
            ))}
          </dl>
        )}
        {event.note && <p className="jt-audit-note">{event.note}</p>}
      </div>
    </li>
  );
}

export function AuditTimeline({ events, label, empty }: {
  events: readonly AuditEvent[];
  /** Names the list, e.g. "ประวัติการเปลี่ยนแปลงบัญชีรับเงิน". */
  label: string;
  empty?: ReactNode;
}) {
  if (events.length === 0) {
    return <p className="jt-field-hint">{empty ?? 'ยังไม่มีประวัติสำหรับรายการนี้'}</p>;
  }
  return (
    <ol className="jt-audit" aria-label={label}>
      {events.map((event) => <AuditTimelineEvent key={event.id} event={event} />)}
    </ol>
  );
}
